// A phone held sideways: one thin 32px bar instead of the tall header, the left menu slides over the page, and a
// "full screen" focus mode. Desktop, laptops (however small or short the window), tablets and phones held upright are
// never touched:
//  - "sideways" is read from the phone's SCREEN: a touch screen whose short side is 500px or less, turned to landscape,
//    with a page area at most 500px high. A desktop or laptop window (even 1280 x 600, or resized short) and a tablet in
//    either position never qualify; nor does an upright phone whose keyboard is open or whose browser bars move;
//  - until a phone is sideways nothing is added: no stylesheet (phoneland.css is loaded only then), no extra page parts
//    (the bar's buttons, the menu's top block and the round button are put in only then), no event listeners beyond
//    the one check for the phone turning. Turning upright again removes all of it, without reloading (typing is kept).
//
// Nothing is removed: the page's filters open in a small panel from the filter button, its action buttons are reached
// from the bar (one or two directly, more in the ⋮ menu; the bar buttons press the page's own buttons), search opens
// over the bar, and the date / time, workspace badge and support-access note move into the slide-over menu.
import { esc } from "./lib.js";
import { navigate, onAction, state } from "./state.js";

/** Is this a phone (touch screen, short side of the screen 500px or less) turned sideways, with a short page area? */
export function phoneSideways(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  if (!window.matchMedia("(pointer: coarse)").matches) return false;
  if (Math.min(screen.width, screen.height) > 500) return false;   // computers, laptops and tablets
  const type = (screen as any).orientation?.type as string | undefined;
  const turned = type ? type.startsWith("landscape") : typeof (window as any).orientation === "number" ? Math.abs((window as any).orientation) === 90 : false;
  return turned && window.innerHeight <= 500 && window.innerWidth > window.innerHeight;
}
export const isPhoneLandscape = () => document.documentElement.classList.contains("pl");

const SVG = {
  filter: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 5h18l-7 8v6l-4 2v-8z"/></svg>',
  search: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M20 20l-4-4"/></svg>',
  full: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>',
  more: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="5" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="12" cy="19" r="1.6"/></svg>',
  restore: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5"/></svg>',
};

// ---- Turning it on and off ------------------------------------------------------------------------------------
function setViewportFit(cover: boolean): void {
  // The notch / rounded corners on the short sides: let the page use them, with padding from env(safe-area-inset-*).
  const m = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (!m) return;
  const base = m.content.replace(/,\s*viewport-fit=\w+/, "");
  m.content = cover ? `${base}, viewport-fit=cover` : base;
}
/** The sideways stylesheet, added only while a phone is sideways (same version as the app's other stylesheets). */
function setStylesheet(on: boolean): void {
  const old = document.getElementById("pl-css");
  if (!on) { old?.remove(); return; }
  if (old) return;
  const ver = document.querySelector<HTMLLinkElement>('link[href*="/css/platform.css"]')?.href.split("?v=")[1] || "";
  const link = document.createElement("link");
  link.id = "pl-css";
  link.rel = "stylesheet";
  link.href = `/css/phoneland.css${ver ? `?v=${ver}` : ""}`;
  document.head.appendChild(link);
}

let listeners: AbortController | null = null, rootObserver: MutationObserver | null = null;
function apply(): void {
  const on = phoneSideways();
  const was = isPhoneLandscape();
  if (on === was) { if (on) decorate(); return; }
  if (on) {
    setStylesheet(true);
    setViewportFit(true);
    document.documentElement.classList.add("pl");
    install();
    decorate();
  } else {
    // Back to upright: everything the sideways layout added is taken away again; the page itself is not redrawn.
    document.documentElement.classList.remove("pl");
    uninstall();
    undecorate();
    setViewportFit(false);
    setStylesheet(false);
  }
  window.dispatchEvent(new Event("falcon:phoneland"));
}

// ---- The parts added to the page while sideways ---------------------------------------------------------------------
const mark = (html: string) => html.replace(/^<(\w+)/, '<$1 data-pl-added=""');
function toolsHtml(withSearch: boolean): string {
  return mark(`<span class="pl-proxies" id="pl-proxies"></span>`)
    + mark(`<button type="button" class="pl-icon pl-filter-btn" id="pl-filter-btn" data-action="pl-filters" aria-label="Filters" title="Filters" aria-expanded="false" hidden>${SVG.filter}<span class="pl-dot" hidden></span></button>`)
    + (withSearch ? mark(`<button type="button" class="pl-icon pl-search-btn" data-action="pl-search" aria-label="Search" title="Search">${SVG.search}</button>`) : "")
    + mark(`<button type="button" class="pl-icon pl-full-btn" data-action="pl-focus" aria-label="Full screen" title="Full screen">${SVG.full}</button>`)
    + mark(`<span class="pl-more-wrap" id="pl-more-wrap" hidden><button type="button" class="pl-icon pl-more-btn" data-action="pl-more" aria-label="More actions" title="More actions" aria-haspopup="menu" aria-expanded="false">${SVG.more}</button><span class="pl-more-pop" id="pl-more-pop" role="menu" hidden></span></span>`);
}
function menuInfoHtml(): string {
  const s = state.session, inConsole = s.mode === "platform", org = s.organisation;
  const subtitle = document.getElementById("page-subtitle")?.textContent || "";
  return mark(`<div class="pl-menu-info">
${subtitle ? `<p class="pl-menu-sub">${esc(subtitle)}</p>` : ""}
<div class="pl-menu-ws">${inConsole ? '<span class="workspace-context-badge platform">Falcon Admin Console</span>' : `<span class="workspace-context-badge organisation">Organisation Workspace</span><strong>${esc(org?.name || "")}</strong>`}</div>
<div class="pl-clock-slot"></div>
${s.supportAccess && !inConsole ? `<p class="pl-menu-support"><strong>Falcon support access</strong> Everything you do here is recorded in ${esc(org?.name || "this organisation")}'s Activity Log.</p>` : ""}
</div>`);
}

let shownPath = "";
/** Put the sideways parts into the page drawn by the shell (again after each page change). */
function decorate(): void {
  if (!isPhoneLandscape()) return;
  const top = document.querySelector(".topbar .top-actions");
  if (top && !top.querySelector("[data-pl-added]")) {
    const s = state.session, inConsole = s.mode === "platform", org = s.organisation;
    const inspector = s.membership?.role === "QC inspector";
    top.insertAdjacentHTML("afterbegin", toolsHtml(!inConsole && !!org && !inspector));
    document.getElementById("page-title")?.insertAdjacentHTML("afterend", mark(`<span class="pl-org">${esc(inConsole ? "Falcon Admin Console" : org?.name || "")}</span>`));
    document.querySelector(".falcon-global-search")?.insertAdjacentHTML("beforeend", mark(`<button type="button" class="pl-search-close" data-action="pl-search-close" aria-label="Close search">×</button>`));
    document.getElementById("nav")?.insertAdjacentHTML("beforebegin", menuInfoHtml());
    document.querySelector(".app")?.insertAdjacentHTML("beforeend", mark(`<button type="button" class="pl-restore" data-action="pl-unfocus" title="Show the bar again" aria-label="Show the bar again">${SVG.restore}</button>`));
    targets = [];
  }
  // A warning line under the bar can be hidden for this visit.
  document.querySelectorAll<HTMLElement>(".falcon-announcement").forEach((a) => {
    const key = a.querySelector("span")?.textContent || "";
    if (hiddenLines.has(key)) { a.remove(); return; }
    if (!a.querySelector('[data-action="dismiss-announcement"], .pl-line-x')) a.insertAdjacentHTML("beforeend", mark(`<button type="button" class="pl-line-x" data-action="pl-hide-line" aria-label="Hide this message">×</button>`));
  });
  if (location.pathname !== shownPath) {
    // Focus mode ends when the person moves to another page.
    shownPath = location.pathname;
    document.body.classList.remove("pl-focus", "pl-filters-open", "pl-search-open");
  }
  arrange();
}
/** Take every sideways part away again (the phone was turned upright). */
function undecorate(): void {
  restoreClock();
  document.querySelectorAll("[data-pl-added]").forEach((el) => el.remove());
  document.querySelectorAll("[data-pl-filterbar]").forEach((el) => el.removeAttribute("data-pl-filterbar"));
  document.querySelectorAll<HTMLElement>("[data-pl-text]").forEach((el) => { delete el.dataset.plText; });
  document.body.classList.remove("pl-focus", "pl-filters-open", "pl-search-open", "falcon-menu-open");
  targets = [];
  shownPath = "";
}

// The date / time and calendar move into the slide-over menu, and back when the phone is turned upright.
function placeClock(): void {
  const wrap = document.getElementById("header-date-time-wrap"), slot = document.querySelector(".pl-clock-slot");
  if (wrap && slot && wrap.parentElement !== slot) { wrap.dataset.plMoved = "1"; slot.appendChild(wrap); }
}
function restoreClock(): void {
  const wrap = document.getElementById("header-date-time-wrap"), home = document.querySelector(".top-actions");
  // Back before the language button, which stays last (top right).
  if (wrap?.dataset.plMoved && home) { delete wrap.dataset.plMoved; home.insertBefore(wrap, home.querySelector(".falcon-lang-wrap")); }
}

// ---- Page actions and filters -------------------------------------------------------------------------------------
let targets: HTMLElement[] = [];
const FILTER_BARS = ".master-filterbar, .cp-toolbar, [data-pl-filters]";
/** Where a page keeps its main buttons (the first one on the page is used). */
const ACTION_BOXES = ".toolbar, .falcon-po-actions, .art-head-actions, .fin-actions, .tp-banner-actions, .tl-actions, .shipment-control-toolbar-actions, .packing-list-actions-top, [data-pl-actions]";
const inModal = (el: Element) => !!el.closest("#modal, .modal, .help-panel");

/** The page's filter bar: the first list / Time & Action Plan filter row on the page. */
function filterBar(): HTMLElement | null {
  return [...document.querySelectorAll<HTMLElement>(`#content ${FILTER_BARS.split(", ").join(", #content ")}`)].find((el) => !inModal(el)) || null;
}
const label = (el: HTMLElement) => (el.getAttribute("data-pl-label") || el.textContent || el.getAttribute("aria-label") || el.getAttribute("title") || "").replace(/\s+/g, " ").trim();
/** The page's own action buttons: its first button row and the buttons in its filter bar. */
function pageActions(bar: HTMLElement | null): HTMLElement[] {
  const out: HTMLElement[] = [];
  const add = (el: HTMLElement) => { if (!out.includes(el) && label(el) && !el.closest(".falcon-chip, [data-pl-added], .falcon-columns-pop")) out.push(el); };
  const tb = [...document.querySelectorAll<HTMLElement>(ACTION_BOXES.split(", ").map((x) => `#content ${x}`).join(", "))].find((el) => !inModal(el) && !(bar && bar.contains(el)));
  tb?.querySelectorAll<HTMLElement>("button, a[href]").forEach(add);
  bar?.querySelectorAll<HTMLElement>("button, a[href]").forEach(add);
  document.querySelectorAll<HTMLElement>("#content [data-pl-action]").forEach(add);
  return out.filter((el) => !inModal(el));
}

/** Is a filter in use? (text typed in a search box, a choice made in a filter list, a filter chip, a changed tick) */
export function filtersActive(bar: HTMLElement): boolean {
  if (bar.querySelector(".falcon-chip")) return true;
  for (const el of bar.querySelectorAll<HTMLInputElement | HTMLSelectElement>("input, select")) {
    if (el.closest(".falcon-columns-pop") || el.dataset.t === "view" || /sort/i.test(el.id + (el.getAttribute("aria-label") || ""))) continue;
    const def = el.getAttribute("data-pl-default");
    if (el instanceof HTMLInputElement) {
      if (el.type === "checkbox") { if (def !== null && el.checked !== (def === "1")) return true; continue; }
      if (["search", "text"].includes(el.type) && el.value.trim()) return true;
      continue;
    }
    if (def !== null ? el.value !== def : el.selectedIndex > 0 && el.options[0]?.value === "") return true;
  }
  return false;
}

function arrange(): void {
  if (!isPhoneLandscape()) return;
  placeClock();
  const bar = filterBar();
  document.querySelectorAll("[data-pl-filterbar]").forEach((el) => { if (el !== bar) el.removeAttribute("data-pl-filterbar"); });
  if (bar && !bar.hasAttribute("data-pl-filterbar")) bar.setAttribute("data-pl-filterbar", "");
  const fbtn = document.getElementById("pl-filter-btn");
  if (fbtn) {
    fbtn.hidden = !bar;
    fbtn.querySelector<HTMLElement>(".pl-dot")!.hidden = !(bar && filtersActive(bar));
    fbtn.setAttribute("aria-expanded", String(document.body.classList.contains("pl-filters-open")));
  }
  if (!bar) document.body.classList.remove("pl-filters-open");
  // Action buttons: one or two go straight into the bar; more go into the ⋮ menu.
  const list = pageActions(bar);
  const slot = document.getElementById("pl-proxies"), wrap = document.getElementById("pl-more-wrap"), pop = document.getElementById("pl-more-pop");
  if (!slot || !wrap || !pop) return;
  const same = list.length === targets.length && list.every((el, i) => el === targets[i] && el.dataset.plText === label(el));
  if (same) return syncDisabled();
  targets = list;
  list.forEach((el) => { el.dataset.plText = label(el); });
  const proxy = (el: HTMLElement, i: number, cls: string) => `<button type="button" class="${cls}" data-action="pl-proxy" data-i="${i}" title="${esc(label(el))}"${(el as HTMLButtonElement).disabled ? " disabled" : ""}>${esc(label(el))}</button>`;
  if (list.length <= 2) {
    slot.innerHTML = list.map((el, i) => proxy(el, i, "pl-proxy")).join("");
    wrap.hidden = true;
    pop.innerHTML = "";
  } else {
    slot.innerHTML = "";
    wrap.hidden = false;
    pop.innerHTML = list.map((el, i) => proxy(el, i, "pl-more-item")).join("");
  }
}
function syncDisabled(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-action="pl-proxy"]').forEach((b) => { b.disabled = !!(targets[Number(b.dataset.i)] as HTMLButtonElement | undefined)?.disabled; });
}
let soonArrange = 0;
function arrangeSoon(): void { window.clearTimeout(soonArrange); soonArrange = window.setTimeout(arrange, 30); }

onAction("pl-proxy", (el) => {
  const t = targets[Number(el.dataset.i)];
  closeMore();
  if (!t || !t.isConnected) return;
  // A button inside the filter panel (Columns, Save filter…) may open a small pop-up there: open the panel first.
  if (t.closest("[data-pl-filterbar]")) setFilters(true);
  t.click();
});

// ---- Filter panel, search, ⋮ menu ------------------------------------------------------------------------------
function setFilters(open: boolean): void {
  document.body.classList.toggle("pl-filters-open", open);
  document.getElementById("pl-filter-btn")?.setAttribute("aria-expanded", String(open));
  if (open) { document.body.classList.remove("pl-search-open"); closeMore(); }
}
onAction("pl-filters", () => setFilters(!document.body.classList.contains("pl-filters-open")));
onAction("pl-search", () => {
  setFilters(false);
  document.body.classList.add("pl-search-open");
  document.getElementById("global-search")?.focus();
});
onAction("pl-search-close", () => {
  document.body.classList.remove("pl-search-open");
  const box = document.getElementById("global-search-results");
  if (box) box.hidden = true;
});
function closeMore(): void {
  const pop = document.getElementById("pl-more-pop");
  if (pop && !pop.hidden) { pop.hidden = true; document.querySelector(".pl-more-btn")?.setAttribute("aria-expanded", "false"); }
}
onAction("pl-more", (el) => {
  const pop = document.getElementById("pl-more-pop");
  if (!pop) return;
  if (!pop.hidden) return closeMore();
  setFilters(false);
  pop.hidden = false;
  el.setAttribute("aria-expanded", "true");
});

// ---- Focus mode: the page uses the whole screen; the round button brings the bar back ---------------------------------
onAction("pl-focus", () => { setFilters(false); closeMore(); document.body.classList.add("pl-focus"); window.dispatchEvent(new Event("resize")); });
onAction("pl-unfocus", () => { document.body.classList.remove("pl-focus"); window.dispatchEvent(new Event("resize")); });

// ---- A warning line under the bar can be hidden (for this visit) ----------------------------------------------------
const hiddenLines = new Set<string>();
onAction("pl-hide-line", (el) => {
  const a = el.closest(".falcon-announcement");
  hiddenLines.add(a?.querySelector("span")?.textContent || "");
  a?.remove();
});

// ---- Listeners that exist only while a phone is sideways ---------------------------------------------------------------
// The slide-over menu: opening it adds a "menu open" step to the browser history (same address); Back closes the menu
// and stays on the page as it is (nothing re-drawn, so unsaved typing is kept).
let marker = false, swallow = false;
function dropMarker(): void {
  if (!marker) return;
  marker = false;
  swallow = true;
  history.back();
}
function install(): void {
  if (listeners) return;
  listeners = new AbortController();
  const opt = { signal: listeners.signal };
  // Each page change redraws the shell: put the sideways parts back in; any change in the page: re-arrange the bar.
  let queued = false;
  rootObserver = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; decorate(); });
  });
  const root = document.getElementById("root");
  if (root) rootObserver.observe(root, { childList: true, subtree: true });
  // Typing or choosing a filter updates the filter button's dot.
  document.addEventListener("input", arrangeSoon, opt);
  document.addEventListener("change", arrangeSoon, opt);
  document.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    if (!t.closest?.(".pl-more-wrap")) closeMore();
    // A tap outside the filter panel closes it (not on pop-ups opened from it, e.g. the date picker).
    if (document.body.classList.contains("pl-filters-open") && !t.closest?.("[data-pl-filterbar], #pl-filter-btn, .pl-proxy, #modal, .falcon-datepick, .fld-pop") && t.isConnected) setFilters(false);
    if (document.body.classList.contains("pl-search-open") && !t.closest?.(".falcon-global-search, .pl-search-btn")) document.body.classList.remove("pl-search-open");
  }, opt);
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    if (document.body.classList.contains("falcon-menu-open")) dropMarker();
    setFilters(false);
    closeMore();
    document.body.classList.remove("pl-search-open");
  }, { ...opt, capture: true });
  document.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    if (t.closest?.('[data-action="open-menu"]') && !marker) {
      marker = true;
      history.pushState({ plMenu: 1 }, "", location.pathname + location.search + location.hash);
      return;
    }
    if (t.closest?.('[data-action="close-menu"]')) { dropMarker(); return; }
    // Choosing a page in the menu: go there in place of the "menu open" step, so Back returns to the page before.
    const link = t.closest?.<HTMLAnchorElement>(".sidebar a[data-link]");
    if (link && marker && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      marker = false;
      document.body.classList.remove("falcon-menu-open");
      void navigate(link.getAttribute("href") || "/", true);
    }
  }, { ...opt, capture: true });
  // Capturing, so it runs before the page router's own Back handling.
  window.addEventListener("popstate", (e) => {
    if (!marker && !swallow) return;
    marker = false;
    swallow = false;
    document.body.classList.remove("falcon-menu-open");
    e.stopImmediatePropagation();
  }, { ...opt, capture: true });
}
function uninstall(): void {
  listeners?.abort();
  listeners = null;
  rootObserver?.disconnect();
  rootObserver = null;
  marker = false;
  swallow = false;
}

// ---- Start (last, once everything above is defined) ------------------------------------------------------------------
if (typeof window !== "undefined" && window.matchMedia) {
  if (phoneSideways()) apply();
  // The only things listened to everywhere: the phone turning (checked again a little later too, because some phones
  // report the new size only after the turn has finished).
  let t = 0;
  const soon = () => { window.clearTimeout(t); t = window.setTimeout(apply, 60); };
  window.matchMedia("(orientation: landscape)").addEventListener?.("change", soon);
  (screen as any).orientation?.addEventListener?.("change", soon);
  window.addEventListener("orientationchange", () => { soon(); window.setTimeout(apply, 400); });
}
